import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  GraduationCap, Plus, BookOpen, FileText, Send, Sparkles, Loader2,
  ArrowLeft, Trash2, Globe, Lock, ExternalLink, CheckCircle2,
  Mic, Download, Volume2,
} from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import SendToAvatarButton from "@/components/SendToAvatarButton";
import {
  ScreenRecorderPanel, SoraVideoGeneratorPanel, LearningPathsPanel,
} from "@/components/academy/AcademyStudioPanels";
import { api } from "@/lib/api";
import { appConfirm } from "@/components/AppDialogHost";

// ──────────────────────────────────────────────────────────────
// Dashboard
// ──────────────────────────────────────────────────────────────
export default function Academy() {
  const navigate = useNavigate();
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const t = await api.get("/academy/templates");
        if (t.data.plan_locked) setLocked(true);
        const r = await api.get("/academy/dashboard");
        setDash(r.data);
      } catch (e) {
        if (e?.response?.status === 402) setLocked(true);
        else toast.error(e?.response?.data?.detail || "Couldn't load Academy");
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return (
    <div className="p-8">
      <Loader2 className="h-6 w-6 animate-spin text-brand-amber" />
    </div>
  );

  if (locked) return (
    <div className="p-8">
      <PageHeader title="BroadcastFlow Academy" subtitle="AI Tutorial Creator" icon={GraduationCap} />
      <div className="mt-6 rounded-md border border-brand-amber/30 bg-brand-amber/5 p-6 max-w-xl">
        <div className="font-mono text-[11px] uppercase tracking-wider text-brand-amber mb-2">// PRO RADIO / NETWORK PLAN</div>
        <div className="text-sm text-ink-secondary mb-4">
          BroadcastFlow Academy is available on <strong>Pro Radio</strong> and <strong>Network</strong> plans.
          Generate professional tutorials, onboarding videos, and knowledge-base articles from any BroadcastFlow feature using your connected AI providers.
        </div>
        <Link to="/dashboard/billing?tab=plan" className="inline-flex items-center gap-2 rounded-md bg-brand-amber px-4 py-2 type-button-label text-bg amber-glow">
          Upgrade
        </Link>
      </div>
    </div>
  );

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <PageHeader
        title="BroadcastFlow Academy"
        subtitle="AI Tutorial Creator · Powered by your AI Provider Center"
        icon={GraduationCap}
        action={
          <button
            onClick={() => navigate("/dashboard/academy/new")}
            data-testid="new-tutorial-btn"
            className="inline-flex items-center gap-2 rounded-md bg-brand-amber px-4 py-2 type-button-label text-bg amber-glow"
          >
            <Plus className="h-4 w-4" /> New Tutorial
          </button>
        }
      />

      <div className="grid grid-cols-3 gap-3 my-6">
        <KPI label="Total" value={dash?.counts?.total ?? 0} testid="kpi-total" />
        <KPI label="Drafts" value={dash?.counts?.drafts ?? 0} testid="kpi-drafts" />
        <KPI label="Published" value={dash?.counts?.published ?? 0} testid="kpi-published" />
      </div>

      <div className="rounded-md border border-line bg-bg-elevated p-5">
        <h2 className="type-card-meta-label font-mono uppercase tracking-wider text-ink-muted mb-3">// Recent</h2>
        {(dash?.recent || []).length === 0 ? (
          <EmptyState onCreate={() => navigate("/dashboard/academy/new")} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="recent-grid">
            {dash.recent.map((t) => (
              <TutorialCard key={t.tutorial_id} t={t} onClick={() => navigate(`/dashboard/academy/${t.tutorial_id}`)} />
            ))}
          </div>
        )}
      </div>

      {/* Update #133 — Learning Paths panel */}
      <div className="mt-6">
        <LearningPathsPanel tutorials={dash?.recent || []} />
      </div>
    </div>
  );
}

function KPI({ label, value, testid }) {
  return (
    <div className="rounded-md border border-line bg-bg-elevated p-4" data-testid={testid}>
      <div className="type-card-meta-label font-mono uppercase tracking-wider text-ink-muted mb-1">{label}</div>
      <div className="text-2xl font-bold text-brand-amber">{value}</div>
    </div>
  );
}

function EmptyState({ onCreate }) {
  return (
    <div className="py-10 text-center" data-testid="academy-empty">
      <BookOpen className="h-10 w-10 text-ink-muted mx-auto mb-2" />
      <div className="type-body text-ink-secondary mb-3">No tutorials yet. Click <strong>New Tutorial</strong> to launch the wizard.</div>
      <button onClick={onCreate} className="rounded-md border border-brand-amber/40 bg-brand-amber/10 px-4 py-2 type-button-label text-brand-amber">
        Start the 5-step wizard
      </button>
    </div>
  );
}

function TutorialCard({ t, onClick }) {
  const isPub = t.status === "published";
  return (
    <button onClick={onClick} data-testid={`tutorial-card-${t.tutorial_id}`}
      className="text-left rounded-md border border-line bg-bg p-4 hover:border-brand-amber/50 transition">
      <div className="flex items-center justify-between mb-1">
        <span className="font-mono type-status-badge uppercase tracking-wider text-ink-muted">{t.feature}</span>
        <span className={`type-status-badge font-mono uppercase px-2 py-0.5 rounded-full border ${
          isPub ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                : "border-brand-amber/40 bg-brand-amber/10 text-brand-amber"
        }`}>{isPub ? "Published" : "Draft"}</span>
      </div>
      <div className="type-list-title text-ink-primary mb-1">{t.title}</div>
      <div className="type-list-subtitle text-ink-muted">{t.tutorial_type} · {t.audience} · {t.difficulty} · {t.duration_minutes} min</div>
    </button>
  );
}

// ──────────────────────────────────────────────────────────────
// 5-step Wizard (separate route)
// ──────────────────────────────────────────────────────────────
export function AcademyWizard() {
  const navigate = useNavigate();
  const [opts, setOpts] = useState(null);
  const [step, setStep] = useState(1);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    feature: "", tutorial_type: "", audience: "",
    difficulty: "Beginner", duration_minutes: 5, notes: "", title: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/academy/templates");
        setOpts(r.data);
      } catch { toast.error("Couldn't load wizard data"); }
    })();
  }, []);

  if (!opts) return <div className="p-8"><Loader2 className="h-6 w-6 animate-spin text-brand-amber" /></div>;

  const submit = async () => {
    setCreating(true);
    try {
      const r = await api.post("/academy/tutorials", form);
      toast.success("Draft created — generating script next.");
      navigate(`/dashboard/academy/${r.data.tutorial_id}?autogen=1`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setCreating(false); }
  };

  const stepValid =
    (step === 1 && !!form.feature) ||
    (step === 2 && !!form.tutorial_type) ||
    (step === 3 && !!form.audience) ||
    (step === 4 && !!form.difficulty) ||
    (step === 5 && form.duration_minutes >= 2);

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <Link to="/dashboard/academy/creator" className="text-xs text-ink-muted hover:text-brand-amber inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="h-3 w-3" /> Back to Academy
      </Link>
      <PageHeader title="New Tutorial" subtitle={`Step ${step} of 5`} icon={Sparkles} />

      <div className="my-6 flex items-center gap-2">
        {[1,2,3,4,5].map((n) => (
          <div key={n} className={`h-1.5 flex-1 rounded-full ${n <= step ? "bg-brand-amber" : "bg-line"}`} />
        ))}
      </div>

      <div className="rounded-md border border-line bg-bg-elevated p-6" data-testid={`wizard-step-${step}`}>
        {step === 1 && (
          <PickerStep title="What would you like to teach?" options={opts.features}
            value={form.feature} onChange={(v) => setForm({ ...form, feature: v })}
            testid="step-feature" />
        )}
        {step === 2 && (
          <PickerStep title="Tutorial Type" options={opts.tutorial_types}
            value={form.tutorial_type} onChange={(v) => setForm({ ...form, tutorial_type: v })}
            testid="step-type" />
        )}
        {step === 3 && (
          <PickerStep title="Audience" options={opts.audiences}
            value={form.audience} onChange={(v) => setForm({ ...form, audience: v })}
            testid="step-audience" />
        )}
        {step === 4 && (
          <PickerStep title="Difficulty" options={opts.difficulties}
            value={form.difficulty} onChange={(v) => setForm({ ...form, difficulty: v })}
            testid="step-difficulty" />
        )}
        {step === 5 && (
          <div data-testid="step-duration">
            <h3 className="type-section-heading text-ink-primary mb-3">Target Duration</h3>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-4">
              {opts.durations_minutes.map((d) => (
                <button key={d} type="button"
                  onClick={() => setForm({ ...form, duration_minutes: d })}
                  data-testid={`duration-${d}`}
                  className={`rounded-md border px-3 py-2 type-button-label ${
                    form.duration_minutes === d ? "border-brand-amber bg-brand-amber/10 text-brand-amber" : "border-line bg-bg text-ink-secondary"
                  }`}>
                  {d} min
                </button>
              ))}
            </div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">Notes for the AI (optional)</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3} maxLength={2000}
              placeholder="Specific topics, gotchas, or angle you want covered…"
              data-testid="step-notes"
              className="w-full mt-1 rounded-md bg-bg border border-line px-3 py-2 text-sm focus:border-brand-amber/60 focus:outline-none"
            />
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mt-4">
        <button onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1}
          data-testid="wizard-prev"
          className="type-button-label px-3 py-1.5 rounded-md border border-line disabled:opacity-30">
          Previous
        </button>
        {step < 5 ? (
          <button onClick={() => setStep(step + 1)} disabled={!stepValid}
            data-testid="wizard-next"
            className="type-button-label px-4 py-2 rounded-md bg-brand-amber text-bg disabled:opacity-40 amber-glow">
            Next
          </button>
        ) : (
          <button onClick={submit} disabled={creating || !stepValid}
            data-testid="wizard-finish"
            className="inline-flex items-center gap-2 type-button-label px-4 py-2 rounded-md bg-brand-amber text-bg disabled:opacity-40 amber-glow">
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            Create draft & generate script
          </button>
        )}
      </div>
    </div>
  );
}

function PickerStep({ title, options, value, onChange, testid }) {
  return (
    <div data-testid={testid}>
      <h3 className="type-section-heading text-ink-primary mb-3">{title}</h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {options.map((o) => (
          <button key={o} type="button" onClick={() => onChange(o)}
            data-testid={`opt-${o.replace(/\s+/g, "-").toLowerCase()}`}
            className={`text-left rounded-md border px-3 py-2 type-button-label ${
              value === o ? "border-brand-amber bg-brand-amber/10 text-brand-amber" : "border-line bg-bg text-ink-secondary hover:border-brand-amber/30"
            }`}>
            {o}
          </button>
        ))}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Tutorial editor (single tutorial view)
// ──────────────────────────────────────────────────────────────
export function AcademyTutorial() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [t, setT] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publicUrl, setPublicUrl] = useState("");
  const [exportingScorm, setExportingScorm] = useState(false);
  // Voiceover state
  const [voiceOpts, setVoiceOpts] = useState([]);
  const [voice, setVoice] = useState("nova");
  const [speed, setSpeed] = useState(1.0);
  const [model, setModel] = useState("tts-1");
  const [generatingVoice, setGeneratingVoice] = useState(false);
  const [audioUrl, setAudioUrl] = useState("");
  // When checked, voiceover auto-renders right after a script generation
  // succeeds — turns the wizard → editor flow into one tap.
  const [autoVoice, setAutoVoice] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/academy/tutorials/${id}`);
      setT(r.data);
      if (r.data.voice?.voice) setVoice(r.data.voice.voice);
      if (r.data.status === "published" && r.data.slug) {
        setPublicUrl(`/academy/${r.data.tutorial_id.slice(0, 0)}${r.data.slug}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Tutorial not found");
      navigate("/dashboard/academy/creator");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Load voice catalog once
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/academy/voices");
        setVoiceOpts(r.data?.voices || []);
      } catch { /* silently ignore — UI falls back to "nova" */ }
    })();
  }, []);

  // Auth-fetch the MP3 when voice meta exists, build a blob URL for <audio>
  useEffect(() => {
    let cancelled = false;
    let createdUrl = "";
    (async () => {
      if (!t?.voice?.voice) { setAudioUrl(""); return; }
      try {
        const resp = await api.get(`/academy/tutorials/${id}/voice.mp3`, { responseType: "blob" });
        if (cancelled) return;
        createdUrl = URL.createObjectURL(resp.data);
        setAudioUrl(createdUrl);
      } catch { /* swallow — UI will show "Generate voice" again */ }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
    // eslint-disable-next-line
  }, [t?.voice?.generated_at, id]);

  // Auto-generate if URL has ?autogen=1
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get("autogen") === "1" && t && !generating && !(t.content?.steps?.length)) {
      generate();
      url.searchParams.delete("autogen");
      window.history.replaceState({}, "", url.toString());
    }
    // eslint-disable-next-line
  }, [t]);

  const generate = async () => {
    setGenerating(true);
    toast.message("Generating script — Claude Sonnet 4.5 is writing…", { duration: 8000 });
    try {
      const r = await api.post(`/academy/tutorials/${id}/generate`);
      setT(r.data);
      toast.success("Script generated.");
      if (autoVoice) {
        // Chain into voice generation. Pass the fresh doc directly so we
        // don't race against React state ((t still reflects the pre-gen value).
        await renderVoice(r.data);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Generation failed", { duration: 9000 }); }
    finally { setGenerating(false); }
  };

  const publish = async () => {
    setPublishing(true);
    try {
      const r = await api.post(`/academy/tutorials/${id}/publish`);
      setPublicUrl(r.data.public_url);
      await load();
      toast.success("Tutorial published.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Publish failed"); }
    finally { setPublishing(false); }
  };

  const unpublish = async () => {
    try { await api.post(`/academy/tutorials/${id}/unpublish`); await load(); toast.success("Moved back to draft."); }
    catch (e) { toast.error(e?.response?.data?.detail || "Unpublish failed"); }
  };

  // Update #179 · LMS export — downloads a SCORM 1.2 ZIP that any
  // SCORM-compliant LMS (Moodle, Docebo, TalentLMS, Canvas, …) can ingest.
  const exportScorm = async () => {
    if (exportingScorm) return;
    setExportingScorm(true);
    toast.message("Building SCORM package…");
    try {
      const resp = await api.post(
        `/academy/tutorials/${id}/export/scorm`,
        {},
        { responseType: "blob" },
      );
      const blob = new Blob([resp.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `academy-${t?.slug || t?.tutorial_id || "tutorial"}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("SCORM package downloaded");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 400) toast.error("Generate the tutorial content first.");
      else if (status === 402) toast.error("Academy is a Pro Radio / Network / Trial feature.");
      else toast.error(e?.response?.data?.detail || "SCORM export failed");
    } finally { setExportingScorm(false); }
  };

  const deleteThis = async () => {
    if (!await appConfirm("Delete this tutorial?")) return;
    try { await api.delete(`/academy/tutorials/${id}`); navigate("/dashboard/academy/creator"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const renderVoice = async (sourceTutorial) => {
    // sourceTutorial may be passed by chain callers to avoid stale state.
    const doc = sourceTutorial || t;
    if (!doc?.content?.steps?.length) {
      toast.error("Generate the script first.");
      return;
    }
    setGeneratingVoice(true);
    toast.message("Synthesising narration — OpenAI TTS is rendering…", { duration: 8000 });
    try {
      await api.post(`/academy/tutorials/${id}/voice`, { voice, speed, model });
      toast.success("Voiceover ready.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Voice generation failed", { duration: 9000 });
    } finally { setGeneratingVoice(false); }
  };

  const generateVoice = () => renderVoice();

  if (loading || !t) return <div className="p-8"><Loader2 className="h-6 w-6 animate-spin text-brand-amber" /></div>;

  const c = t.content || {};
  const hasContent = (c.steps || []).length > 0;
  const isPub = t.status === "published";
  const flattenedScript = hasContent ? flattenAcademyScript(c) : "";

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <Link to="/dashboard/academy/creator" className="text-xs text-ink-muted hover:text-brand-amber inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="h-3 w-3" /> Back to Academy
      </Link>
      <div className="flex items-start justify-between mb-2 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold">{t.title}</h1>
          <div className="text-[11px] font-mono uppercase tracking-wider text-ink-muted mt-1">
            {t.feature} · {t.tutorial_type} · {t.audience} · {t.difficulty} · {t.duration_minutes} min
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-[10px] font-mono uppercase px-2 py-1 rounded-full border ${
            isPub ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                  : "border-brand-amber/40 bg-brand-amber/10 text-brand-amber"
          }`}>{isPub ? "Published" : "Draft"}</span>
          <button onClick={deleteThis} data-testid="delete-btn"
            className="text-xs px-2 py-1 rounded-md border border-brand-onair/30 text-brand-onair hover:bg-brand-onair/10">
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      </div>

      {/* Actions row */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <button onClick={generate} disabled={generating || generatingVoice} data-testid="generate-btn"
          className="inline-flex items-center gap-2 type-button-label px-4 py-2 rounded-md bg-brand-amber text-bg disabled:opacity-50 amber-glow">
          {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {hasContent ? "Regenerate script" : "Generate script"}
        </button>
        <label className="inline-flex items-center gap-2 text-xs text-ink-secondary cursor-pointer select-none"
               title="When checked, voiceover renders automatically right after the script is generated.">
          <input
            type="checkbox"
            checked={autoVoice}
            onChange={(e) => setAutoVoice(e.target.checked)}
            data-testid="auto-voice-toggle"
            className="accent-brand-amber"
          />
          <span>Auto-voice</span>
        </label>
        {hasContent && !isPub && (
          <button onClick={publish} disabled={publishing} data-testid="publish-btn"
            className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 disabled:opacity-50">
            {publishing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Publish public reader
          </button>
        )}
        {isPub && (
          <>
            <button onClick={unpublish} data-testid="unpublish-btn"
              className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-md border border-line">
              <Lock className="h-4 w-4" /> Unpublish
            </button>
            {publicUrl && (
              <a href={publicUrl} target="_blank" rel="noopener noreferrer" data-testid="public-link"
                className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-md border border-line hover:border-brand-amber/40">
                <ExternalLink className="h-4 w-4" /> Open public page
              </a>
            )}
          </>
        )}
        {hasContent && (
          <SendToAvatarButton
            script={flattenedScript}
            title={t.title}
            testid="academy-send-to-avatar"
          />
        )}
        {hasContent && (
          <button
            onClick={exportScorm}
            disabled={exportingScorm}
            data-testid="academy-scorm-export-btn"
            title="Download a SCORM 1.2 ZIP — upload it into any SCORM-compliant LMS (Moodle, Docebo, TalentLMS, Canvas, Blackboard, Cornerstone, …)"
            className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-md border border-cyan-500/40 bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20 disabled:opacity-50 transition"
          >
            {exportingScorm ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Export to LMS (SCORM)
          </button>
        )}
      </div>

      {t.provider_used && (
        <div className="text-[10px] font-mono text-ink-muted mb-4">
          Powered by {t.provider_used.provider_key} ({t.provider_used.model})
        </div>
      )}

      {/* Update #133 — Screen Recording Assistant + Sora 2 video generator */}
      <div className="mb-6 space-y-4">
        <ScreenRecorderPanel
          tutorialId={id}
          existingRecording={t.recording}
          onUploaded={() => load()}
        />
        <SoraVideoGeneratorPanel
          tutorialId={id}
          existingVideo={t.generated_video}
          onGenerated={() => load()}
        />
      </div>

      {/* ───── Voiceover card ───── */}
      {hasContent && (
        <VoiceoverCard
          tutorial={t}
          voice={voice} setVoice={setVoice}
          speed={speed} setSpeed={setSpeed}
          model={model} setModel={setModel}
          voiceOpts={voiceOpts}
          generating={generatingVoice}
          onGenerate={generateVoice}
          audioUrl={audioUrl}
          downloadHref={`/academy/tutorials/${id}/voice.mp3`}
        />
      )}

      {!hasContent ? (
        <div className="rounded-md border border-dashed border-brand-amber/30 bg-brand-amber/5 p-8 text-center" data-testid="awaiting-gen">
          <FileText className="h-10 w-10 text-brand-amber mx-auto mb-2" />
          <div className="text-sm text-ink-secondary">No script yet — click <strong>Generate script</strong> to let your AI Provider Center pen this tutorial.</div>
        </div>
      ) : (
        <article className="rounded-md border border-line bg-bg-elevated p-6 space-y-5" data-testid="tutorial-content">
          {c.intro && <Section title="Intro"><p className="leading-relaxed">{c.intro}</p></Section>}
          {c.objectives?.length > 0 && (
            <Section title="Learning objectives">
              <ul className="list-disc pl-5 space-y-1">
                {c.objectives.map((o, i) => <li key={i}>{o}</li>)}
              </ul>
            </Section>
          )}
          {c.steps?.length > 0 && (
            <Section title="Steps">
              <ol className="space-y-3 list-none">
                {c.steps.map((s, i) => (
                  <li key={i} className="rounded-md border border-line bg-bg p-3">
                    <div className="type-list-title text-ink-primary mb-1">{s.title}</div>
                    <div className="type-body text-ink-secondary">{s.body}</div>
                  </li>
                ))}
              </ol>
            </Section>
          )}
          {c.tips?.length > 0 && (
            <Section title="Tips">
              <ul className="list-disc pl-5 space-y-1 text-sm">{c.tips.map((x, i) => <li key={i}>{x}</li>)}</ul>
            </Section>
          )}
          {c.warnings?.length > 0 && (
            <Section title="Warnings">
              <ul className="list-disc pl-5 space-y-1 text-sm text-brand-amber">{c.warnings.map((x, i) => <li key={i}>{x}</li>)}</ul>
            </Section>
          )}
          {c.summary && <Section title="Summary"><p>{c.summary}</p></Section>}
          {c.cta && (
            <Section title="Next">
              <p className="text-sm font-mono text-brand-amber">→ {c.cta}</p>
            </Section>
          )}
          {c.quiz?.length > 0 && (
            <Section title={`Quiz (${c.quiz.length})`}>
              <ol className="space-y-3">
                {c.quiz.map((q, i) => (
                  <li key={i} className="rounded-md border border-line bg-bg p-3">
                    <div className="font-medium mb-2">Q{i + 1}. {q.q}</div>
                    <ol className="list-[upper-alpha] pl-5 space-y-1 text-sm">
                      {(q.options || []).map((opt, oi) => (
                        <li key={oi} className={oi === q.answer_index ? "text-emerald-300" : ""}>
                          {opt}{oi === q.answer_index && <CheckCircle2 className="inline h-3 w-3 ml-1" />}
                        </li>
                      ))}
                    </ol>
                    {q.explain && <div className="text-[11px] text-ink-muted mt-2">{q.explain}</div>}
                  </li>
                ))}
              </ol>
            </Section>
          )}
        </article>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h3 className="text-xs font-mono uppercase tracking-wider text-brand-amber mb-2">// {title}</h3>
      {children}
    </section>
  );
}

// ──────────────────────────────────────────────────────────────
// Voiceover card — OpenAI TTS narration generator (Phase 2)
// ──────────────────────────────────────────────────────────────
function VoiceoverCard({ tutorial, voice, setVoice, speed, setSpeed, model, setModel,
                        voiceOpts, generating, onGenerate, audioUrl, downloadHref }) {
  const v = tutorial.voice;
  const hasVoice = !!(v && v.voice);
  const sizeKB = hasVoice && v.size_bytes ? Math.round(v.size_bytes / 1024) : 0;
  const durSec = hasVoice ? Math.round(v.estimated_duration_seconds || 0) : 0;
  const mins = Math.floor(durSec / 60);
  const secs = durSec % 60;

  const downloadMp3 = async () => {
    try {
      const resp = await api.get(downloadHref, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      const safe = (tutorial.title || "tutorial").replace(/[^A-Za-z0-9._-]+/g, "-");
      a.download = `${safe}.mp3`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't download MP3");
    }
  };

  return (
    <div className="rounded-md border border-line bg-bg-elevated p-5 mb-5" data-testid="voiceover-card">
      <div className="flex items-center gap-2 mb-3">
        <Mic className="h-4 w-4 text-brand-amber" />
        <h3 className="text-xs font-mono uppercase tracking-wider text-brand-amber">// Voiceover</h3>
        {hasVoice && (
          <span className="ml-2 text-[10px] font-mono px-2 py-0.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 text-emerald-300">
            Ready · ~{mins}m {secs}s · {sizeKB} KB
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <div>
          <label className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">Voice</label>
          <select
            value={voice}
            onChange={(e) => setVoice(e.target.value)}
            data-testid="voice-select"
            className="w-full mt-1 rounded-md bg-bg border border-line px-3 py-2 text-sm focus:border-brand-amber/60 focus:outline-none"
          >
            {(voiceOpts.length ? voiceOpts : [
              { id: "nova", label: "Friendly female (default)" },
            ]).map((o) => (
              <option key={o.id} value={o.id}>{o.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">Speed</label>
          <select
            value={speed}
            onChange={(e) => setSpeed(parseFloat(e.target.value))}
            data-testid="voice-speed"
            className="w-full mt-1 rounded-md bg-bg border border-line px-3 py-2 text-sm focus:border-brand-amber/60 focus:outline-none"
          >
            {[0.75, 0.9, 1.0, 1.1, 1.25].map((s) => (
              <option key={s} value={s}>{`${s.toFixed(2)}×`}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">Quality</label>
          <select
            value={model}
            onChange={(e) => setModel(e.target.value)}
            data-testid="voice-model"
            className="w-full mt-1 rounded-md bg-bg border border-line px-3 py-2 text-sm focus:border-brand-amber/60 focus:outline-none"
          >
            <option value="tts-1">Standard (faster)</option>
            <option value="tts-1-hd">HD (richer)</option>
          </select>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={onGenerate}
          disabled={generating}
          data-testid="generate-voice-btn"
          className="inline-flex items-center gap-2 type-button-label px-4 py-2 rounded-md bg-brand-amber text-bg disabled:opacity-50 amber-glow"
        >
          {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Volume2 className="h-4 w-4" />}
          {hasVoice ? "Regenerate voiceover" : "Generate voiceover"}
        </button>
        {hasVoice && (
          <button
            onClick={downloadMp3}
            data-testid="download-voice-btn"
            className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-md border border-line hover:border-brand-amber/40"
          >
            <Download className="h-4 w-4" /> Download MP3
          </button>
        )}
      </div>

      {hasVoice && audioUrl && (
        <audio
          controls
          src={audioUrl}
          data-testid="voice-audio"
          className="w-full mt-4"
        />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Flatten the structured tutorial content into a single narration
// string. Mirrors the backend's `_flatten_script()` semantics so the
// Send-to-Avatar payload matches what TTS would render.
// ──────────────────────────────────────────────────────────────
function flattenAcademyScript(content) {
  const parts = [];
  if (content.intro) parts.push(String(content.intro).trim());
  if (content.objectives?.length) {
    parts.push(
      "In this tutorial, you will learn the following: " +
      content.objectives.map((o) => String(o).trim().replace(/\.$/, "")).join(". ") + "."
    );
  }
  (content.steps || []).forEach((s) => {
    const title = String(s.title || "").trim();
    const body = String(s.body || "").trim();
    if (title && body) parts.push(`${title}. ${body}`);
    else if (body) parts.push(body);
  });
  if (content.tips?.length) {
    parts.push("A few tips. " + content.tips.map((t) => String(t).trim().replace(/\.$/, "") + ".").join(" "));
  }
  if (content.warnings?.length) {
    parts.push("Important warnings. " + content.warnings.map((w) => String(w).trim().replace(/\.$/, "") + ".").join(" "));
  }
  if (content.summary) parts.push(String(content.summary).trim());
  if (content.cta) parts.push(String(content.cta).trim());
  return parts.join("\n\n");
}

// ──────────────────────────────────────────────────────────────
// Public library landing page (no auth) — /academy/:station_id
// ──────────────────────────────────────────────────────────────
export function AcademyLibrary() {
  const { station_id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [query, setQuery] = useState("");

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/academy/public/${station_id}`)
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(setData)
      .catch(() => setErr("Station not found"));
  }, [station_id]);

  if (err) return <div className="min-h-screen grid place-items-center text-ink-muted">{err}</div>;
  if (!data) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-brand-amber" /></div>;

  const filtered = (data.items || []).filter((t) => {
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return (t.title || "").toLowerCase().includes(q)
        || (t.feature || "").toLowerCase().includes(q)
        || (t.tutorial_type || "").toLowerCase().includes(q);
  });

  return (
    <div className="min-h-screen bg-bg text-ink-primary py-12 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-3">
          <GraduationCap className="h-3 w-3 text-brand-amber" />
          <span>{data.station_name} · BroadcastFlow Academy</span>
        </div>
        <h1 className="text-5xl font-bold mb-2">{data.station_name}</h1>
        {data.station_tagline && (
          <p className="text-lg text-ink-secondary mb-8">{data.station_tagline}</p>
        )}

        <div className="flex items-center gap-3 mb-8">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search tutorials…"
            data-testid="library-search"
            className="flex-1 max-w-md rounded-md bg-bg-elevated border border-line px-4 py-2 text-sm focus:border-brand-amber/60 focus:outline-none"
          />
          <span className="text-[11px] font-mono text-ink-muted">{filtered.length} of {data.total}</span>
        </div>

        {data.total === 0 ? (
          <div className="rounded-md border border-dashed border-line p-12 text-center" data-testid="library-empty">
            <BookOpen className="h-10 w-10 text-ink-muted mx-auto mb-3" />
            <p className="text-sm text-ink-muted">No published tutorials yet. Check back soon.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="library-grid">
            {filtered.map((t) => (
              <Link
                key={t.tutorial_id}
                to={`/academy/${station_id}/${t.slug}`}
                data-testid={`library-card-${t.tutorial_id}`}
                className="block rounded-md border border-line bg-bg-elevated p-5 hover:border-brand-amber/50 transition group"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="font-mono type-status-badge uppercase tracking-wider text-brand-amber">{t.feature}</span>
                  <span className="font-mono type-status-badge text-ink-muted">{t.duration_minutes} min</span>
                </div>
                <h2 className="type-section-heading text-ink-primary mb-2 group-hover:text-brand-amber transition">{t.title}</h2>
                <div className="font-mono type-list-subtitle uppercase tracking-wider text-ink-muted">
                  {t.tutorial_type} · {t.audience} · {t.difficulty}
                </div>
              </Link>
            ))}
          </div>
        )}

        <div className="mt-16 text-center text-[10px] font-mono text-ink-muted">
          Powered by BroadcastFlow Academy
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────
// Public reader (no auth) — route at /academy/:station_id/:slug
// ──────────────────────────────────────────────────────────────
export function AcademyPublic() {
  const { station_id, slug } = useParams();
  const [t, setT] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/academy/public/${station_id}/${slug}`)
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(setT)
      .catch(() => setErr("Tutorial not found"));
  }, [station_id, slug]);

  if (err) return <div className="min-h-screen grid place-items-center text-ink-muted">{err}</div>;
  if (!t) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-brand-amber" /></div>;

  const c = t.content || {};
  return (
    <div className="min-h-screen bg-bg text-ink-primary py-12 px-4">
      <article className="max-w-3xl mx-auto" data-testid="public-tutorial">
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-3">
          <GraduationCap className="h-3 w-3 text-brand-amber" />
          <span>{t.station_name} · BroadcastFlow Academy</span>
        </div>
        <h1 className="text-4xl font-bold mb-2">{t.title}</h1>
        <div className="text-xs text-ink-muted mb-8">
          {t.feature} · {t.tutorial_type} · {t.audience} · {t.difficulty} · ~{t.duration_minutes} min read
        </div>
        {c.intro && <p className="text-lg leading-relaxed mb-8">{c.intro}</p>}
        {c.objectives?.length > 0 && (
          <div className="rounded-md border border-brand-amber/30 bg-brand-amber/5 p-5 mb-8">
            <h3 className="text-xs font-mono uppercase tracking-wider text-brand-amber mb-3">// You&apos;ll learn</h3>
            <ul className="list-disc pl-5 space-y-1">{c.objectives.map((o, i) => <li key={i}>{o}</li>)}</ul>
          </div>
        )}
        {c.steps?.map((s, i) => (
          <section key={i} className="mb-6">
            <h2 className="text-xl font-bold mb-2">{s.title}</h2>
            <p className="leading-relaxed text-ink-secondary">{s.body}</p>
          </section>
        ))}
        {c.tips?.length > 0 && (
          <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-5 mb-6">
            <h3 className="text-xs font-mono uppercase tracking-wider text-emerald-300 mb-2">// Tips</h3>
            <ul className="list-disc pl-5 space-y-1 text-sm">{c.tips.map((x, i) => <li key={i}>{x}</li>)}</ul>
          </div>
        )}
        {c.warnings?.length > 0 && (
          <div className="rounded-md border border-brand-onair/30 bg-brand-onair/5 p-5 mb-6">
            <h3 className="text-xs font-mono uppercase tracking-wider text-brand-onair mb-2">// Watch out</h3>
            <ul className="list-disc pl-5 space-y-1 text-sm">{c.warnings.map((x, i) => <li key={i}>{x}</li>)}</ul>
          </div>
        )}
        {c.summary && <div className="border-t border-line pt-5 mt-8"><p className="leading-relaxed">{c.summary}</p></div>}
        {c.cta && <p className="text-brand-amber font-mono text-sm mt-4">→ {c.cta}</p>}
        <div className="mt-12 text-[10px] font-mono text-ink-muted text-center">Published via BroadcastFlow Academy</div>
      </article>
    </div>
  );
}
